(() => {
  const CARD_SELECTOR = '.product-card[data-product]';
  const cache = new Map();
  const initialized = new WeakSet();

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const productPath = handle => `${rootPath()}products/${handle}`;

  const fetchProduct = handle => {
    if (!cache.has(handle)) {
      cache.set(handle, fetch(`${productPath(handle)}.js`, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
        .catch(error => {
          cache.delete(handle);
          throw error;
        }));
    }
    return cache.get(handle);
  };

  const optionNames = product => (product.options || []).map(option =>
    typeof option === 'string' ? option : option.name
  );

  const mediaImage = media => {
    if (!media) return null;
    if (media.preview_image) return media.preview_image;
    if (media.src) return media;
    return null;
  };

  const variantImage = variant => {
    if (!variant) return null;
    if (variant.featured_media?.preview_image) return variant.featured_media.preview_image;
    if (variant.featured_image) {
      return typeof variant.featured_image === 'string'
        ? { src: variant.featured_image, alt: '' }
        : variant.featured_image;
    }
    return null;
  };

  const getChoices = product => {
    const colorIndex = optionNames(product).findIndex(name => /^colou?r$/i.test(String(name || '').trim()));
    if (colorIndex < 0) return [];
    console.log({colorIndex, product, optionNames, productPath})

    const groups = new Map();
    (product.variants || []).forEach(variant => {
      const color = variant.options?.[colorIndex];
      if (!color) return;
      if (!groups.has(color)) groups.set(color, []);
      groups.get(color).push(variant);
    });

    return Array.from(groups.entries()).map(([color, variants]) => {
      const available = variants.filter(variant => variant.available);
      const representative = available.find(variant => variantImage(variant))
        || variants.find(variant => variantImage(variant));

      const image = variantImage(representative);
      let hoverImage = image;

      
      const mediaId = representative?.featured_media?.id;
      if (mediaId && Array.isArray(product.media)) {
        const mediaIndex = product.media.findIndex(media => String(media.id) === String(mediaId));
        const nextImage = mediaImage(product.media[mediaIndex + 1]);
        // console.log({representative, image, nextImage})

        if (mediaIndex >= 0 && nextImage?.src) hoverImage = nextImage;
      }

      return {
        color,
        variant: representative,
        image,
        hoverImage
      };
    }).filter(choice => choice.variant && choice.image?.src);
  };

  const updateLinks = (card, url) => {
    card.querySelectorAll('.js-color-swatches-link, .card__title a, a.full-unstyled-link').forEach(link => {
      if (link.tagName === 'A') link.href = url;
    });
  };

  const updateImageElement = (image, src, alt) => {
    if (!image || !src) return;
    image.src = src;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    if (typeof alt === 'string') image.alt = alt;
  };

  const setHoverState = (card, hovering) => {
    const firstImage = card.querySelector('.js-color-swatches-link .media--first');
    const secondImage = card.querySelector('.js-color-swatches-link .media--second');
    if (!firstImage) return;

    firstImage.style.opacity = hovering && secondImage ? '0' : '1';
    firstImage.style.visibility = 'visible';

    if (secondImage) {
      secondImage.style.opacity = hovering ? '1' : '0';
      secondImage.style.visibility = 'visible';
    }
  };

  const updateCardImages = (card, button) => {
    const modelSrc = button.dataset.image;
    const hoverSrc = button.dataset.hoverImage || modelSrc;
    const alt = button.dataset.alt || button.dataset.color || '';

    const firstImage = card.querySelector('.js-color-swatches-link .media--first');
    const secondImage = card.querySelector('.js-color-swatches-link .media--second');

    updateImageElement(secondImage, modelSrc, alt);
    updateImageElement(firstImage, hoverSrc, alt);
    setHoverState(card, false);
  };

  const activate = (card, button) => {
    card.querySelectorAll('.reputation-card-swatch').forEach(swatch => {
      const selected = swatch === button;
      swatch.classList.toggle('is-active', selected);
      swatch.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    updateCardImages(card, button);
    if (button.dataset.url) updateLinks(card, button.dataset.url);
  };

  const buildSwatch = (card, handle, choice, active) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `reputation-card-swatch${active ? ' is-active' : ''}`;
    button.dataset.url = `${productPath(handle)}?variant=${choice.variant.id}`;
    button.dataset.image = choice.image.src;
    button.dataset.hoverImage = choice.hoverImage?.src || choice.image.src;
    button.dataset.alt = choice.image.alt || choice.color;
    button.dataset.color = choice.color;
    button.setAttribute('aria-label', `Select ${choice.color}`);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.title = choice.color;

    const image = document.createElement('img');
    image.className = 'reputation-card-swatch__image';
    image.src = choice.image.src;
    image.alt = '';
    image.loading = 'lazy';
    image.width = 76;
    image.height = 76;
    button.appendChild(image);

    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      activate(card, button);
    });

    return button;
  };

  const mount = async card => {
    if (initialized.has(card)) return;
    initialized.add(card);

    const handle = card.dataset.product;
    if (!handle) return;

    try {
      const product = await fetchProduct(handle);
      const choices = getChoices(product);
      if (choices.length < 1 || !card.isConnected) return;

      const row = document.createElement('div');
      row.className = 'reputation-card-swatches';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', 'Available colors');

      choices.forEach((choice, index) => {
        row.appendChild(buildSwatch(card, handle, choice, index === 0));
      });

      const mediaCard = card.querySelector(':scope > .card');
      const information = card.querySelector(':scope > .card-information');
      if (mediaCard && information) mediaCard.insertAdjacentElement('afterend', row);
      else if (information) information.insertAdjacentElement('beforebegin', row);
      else card.appendChild(row);

      const mediaLink = card.querySelector('.js-color-swatches-link');
      if (mediaLink) {
        mediaLink.addEventListener('mouseenter', () => setHoverState(card, true));
        mediaLink.addEventListener('mouseleave', () => setHoverState(card, false));
        mediaLink.addEventListener('focusin', () => setHoverState(card, true));
        mediaLink.addEventListener('focusout', () => setHoverState(card, false));
      }

      setHoverState(card, false);
      card.classList.add('reputation-swatches-ready');
    } catch (error) {
      console.warn('Collection color swatches could not load for', handle, error);
    }
  };

  const scan = root => {
    const cards = [];
    if (root.nodeType === 1 && root.matches?.(CARD_SELECTOR)) cards.push(root);
    if (root.querySelectorAll) cards.push(...root.querySelectorAll(CARD_SELECTOR));
    cards.forEach(mount);
  };

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) scan(node);
      });
    });
  });

  const start = () => {
    scan(document);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
